import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  DatatableCell,
  DatatableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Switch,
} from '@monksflow/monks-ui';
import { MoreVertical, Pencil, PlugZap, RefreshCw, Trash2 } from 'lucide-react';
import {
  CAPABILITY_LABELS,
  CONNECTOR_STATUS_LABELS,
  CONNECTOR_STATUS_VARIANT,
  SYNC_CADENCE_LABELS,
} from '@/entities/media-connector';
import { CapabilityBadge } from '../../CapabilityBadge';
import { type AccountRowProps, STATUS_BADGE_CLASSES } from './AccountRow.types';

export function AccountRow({
  account,
  syncStatus,
  onEdit,
  onDelete,
  onToggle,
  onSync,
  onTest,
  isToggling,
  isSyncing,
  isTesting,
}: AccountRowProps) {
  const { t } = useTranslation('knowledge');
  const displayTitle =
    account.title ||
    account.externalAccountName ||
    t('mediaConnectors.mediaConnectorDetail.untitledAccount');
  const statusVariant = CONNECTOR_STATUS_VARIANT[account.status];
  const canToggle = account.status === 'connected' || account.status === 'disconnected';

  return (
    <DatatableRow className="h-16">
      <DatatableCell>
        <span className="block truncate text-sm font-medium text-foreground">{displayTitle}</span>
      </DatatableCell>

      <DatatableCell>
        {account.externalAccountId ? (
          <span className="truncate text-sm text-muted-foreground">
            {account.externalAccountId}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </DatatableCell>

      <DatatableCell>
        {account.tags.length > 0 ? (
          <div className="flex items-center gap-1 overflow-hidden">
            {account.tags.slice(0, 5).map((tag) => (
              <Badge
                key={tag}
                variant="ghost"
                roundness="full"
                className="shrink-0 border-border-light px-2 py-0.5 text-xs text-foreground-alt"
              >
                {tag}
              </Badge>
            ))}
            {account.tags.length > 5 && (
              <Badge
                variant="ghost"
                roundness="full"
                className="shrink-0 border-border-light px-2 py-0.5 text-xs text-foreground-alt"
                title={account.tags.slice(5).join(', ')}
              >
                +{account.tags.length - 5}
              </Badge>
            )}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </DatatableCell>

      <DatatableCell>
        <Badge
          variant="ghost"
          roundness="full"
          className={`px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[statusVariant] ?? ''}`}
        >
          {CONNECTOR_STATUS_LABELS[account.status]}
        </Badge>
      </DatatableCell>

      {/* Publishing and reporting only. Sign-in is what the Status cell to the left already
          says, and showing it twice would imply they are separate answers. */}
      <DatatableCell>
        {account.capabilities ? (
          <div className="flex items-center gap-1">
            <CapabilityBadge
              label={CAPABILITY_LABELS.publishing}
              capability={account.capabilities.publishing}
              badgeClasses={STATUS_BADGE_CLASSES}
            />
            <CapabilityBadge
              label={CAPABILITY_LABELS.reporting}
              capability={account.capabilities.reporting}
              badgeClasses={STATUS_BADGE_CLASSES}
            />
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </DatatableCell>

      <DatatableCell>
        {syncStatus?.lastSyncAt ? (
          <span className="text-xs text-muted-foreground">
            {new Date(syncStatus.lastSyncAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </DatatableCell>

      <DatatableCell>
        <span className="text-xs text-muted-foreground">
          {SYNC_CADENCE_LABELS[account.syncCadence]}
        </span>
      </DatatableCell>

      <DatatableCell>
        {canToggle ? (
          <Switch
            checked={account.status === 'connected'}
            onCheckedChange={onToggle}
            disabled={isToggling}
          />
        ) : (
          <span className="text-sm text-muted-foreground">&mdash;</span>
        )}
      </DatatableCell>

      <DatatableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="mini"
              aria-label={t('mediaConnectors.mediaConnectorDetail.accountActions')}
              className="size-9 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            {(account.status === 'connected' || account.status === 'error') && (
              <DropdownMenuItem onSelect={onSync} disabled={isSyncing}>
                <RefreshCw className={`mr-2 size-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing
                  ? t('mediaConnectors.mediaConnectorDetail.syncing')
                  : t('mediaConnectors.mediaConnectorDetail.syncNow')}
              </DropdownMenuItem>
            )}
            {/* Available in every state, unlike Sync Now: the moment you most need to
                ask what still works is when something has stopped. */}
            <DropdownMenuItem onSelect={onTest} disabled={isTesting}>
              <PlugZap className={`mr-2 size-4 ${isTesting ? 'animate-pulse' : ''}`} />
              {isTesting
                ? t('mediaConnectors.mediaConnectorDetail.testing')
                : t('mediaConnectors.mediaConnectorDetail.testConnection')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={onEdit}>
              <Pencil className="mr-2 size-4" />
              {t('mediaConnectors.mediaConnectorDetail.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 size-4" />
              {t('mediaConnectors.mediaConnectorDetail.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </DatatableCell>
    </DatatableRow>
  );
}

AccountRow.displayName = 'AccountRow';
